// ViewTest.js — interactive on-screen view of a test record.
//
// Wraps the shared TestReportContent component so the on-screen view
// uses the same polished layout as the printable PDF report. Adds the
// user-facing controls that don't belong in a PDF:
//
//   - "Edit this test" button (admin/superuser/pro_user)
//   - "Request correction" button + dialog (regular users)
//   - Back navigation
//
// When rendered inside ReportContext (e.g. embedded in a report page),
// the user controls are hidden via isReportMode.

import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { useReportContext } from "./reports/ReportContext";
import {
  getTestResultById,
  getUserProfile,
  getOrganizationDetails,
  getSpecimenTypes,
  getClinicalIndications,
  getSpeciesMaster,
  addCorrectionRequest,
  hasOpenRequest as checkHasOpenRequest,
} from "./firestoreService";
import { loadRevisionHistory } from "./reports/loadRevisionHistory";
import TestReportContent from "./reports/TestReportContent";
import {
  Box,
  Button,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";

function ViewTest() {
  const { userRole, currentUser } = useAuth();
  const reportContext = useReportContext();
  const isReportMode = !!reportContext?.isReportContext;
  const { testId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [testData, setTestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Display-name lookups (parallel to PrintReport)
  const [submitterName, setSubmitterName] = useState("");
  const [editorName, setEditorName] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [primaryGuideline, setPrimaryGuideline] = useState("EUCAST");
  // Raw arrays from the master collections. Lookup maps are derived via
  // useMemo so they rebuild on language change without refetching.
  const [allSpecimenTypes, setAllSpecimenTypes] = useState([]);
  const [allClinicalIndications, setAllClinicalIndications] = useState([]);
  const [allSpecies, setAllSpecies] = useState([]);

  // Revision history for the audit table
  const [revisions, setRevisions] = useState([]);
  const [editorNames, setEditorNames] = useState({});

  // Correction request dialog state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasOpenRequest, setHasOpenRequest] = useState(false);

  const { t, i18n } = useTranslation();

  useEffect(() => {
    const fetchTestData = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getTestResultById(testId);
        if (!data) {
          setError(t("editTest.caseNotFound"));
          setLoading(false);
          return;
        }
        setTestData(data);

        // Parallel fetch for everything else.
        const [
          openRequestExists,
          submitter,
          org,
          specimenTypes,
          clinicalIndications,
          species,
          history,
        ] = await Promise.all([
          checkHasOpenRequest(testId, data.organizationId),
          data.submitterUid ? getUserProfile(data.submitterUid) : null,
          data.organizationId
            ? getOrganizationDetails(data.organizationId)
            : null,
          getSpecimenTypes(),
          getClinicalIndications(),
          getSpeciesMaster(),
          loadRevisionHistory(testId),
        ]);

        setHasOpenRequest(openRequestExists);
        setSubmitterName(submitter?.displayName || "Unknown");
        setOrganizationName(org?.name || "Unknown");
        setPrimaryGuideline(org?.primaryGuideline || "EUCAST");

        // Store raw master-data arrays. Lookup maps are derived below via
        // useMemo so they rebuild on language change without refetching.
        setAllSpecimenTypes(specimenTypes || []);
        setAllClinicalIndications(clinicalIndications || []);
        setAllSpecies(species || []);

        setRevisions(history.revisions);
        setEditorNames(history.editorNames);

        // Resolve last editor's display name (if edited). Tolerates the
        // legacy field name `lastEditedBy` AND Phase 3 `lastEditedByUid`.
        const editorUid = data.lastEditedByUid || data.lastEditedBy;
        if (data.isEdited && editorUid && typeof editorUid === "string") {
          try {
            const editorProfile = await getUserProfile(editorUid);
            setEditorName(editorProfile?.displayName || "Unknown user");
          } catch (e) {
            console.warn("Failed to look up last editor:", e);
            setEditorName("Unknown user");
          }
        }
      } catch (err) {
        console.error(err);
        setError(t("viewTest.loadError"));
      } finally {
        setLoading(false);
      }
    };
    fetchTestData();
  }, [testId, t]);

  const handleRequestCorrection = () => setIsModalOpen(true);
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setReason("");
  };

  const handleSubmitRequest = async () => {
    if (!reason.trim()) {
      alert(t("viewTest.provideReason"));
      return;
    }
    setIsSubmitting(true);
    try {
      await addCorrectionRequest({
        testId: testData.id,
        organizationId: testData.organizationId,
        reason: reason.trim(),
        requestedByUid: currentUser.uid,
      });
      alert(t("viewTest.requestSubmitted"));
      navigate(-1);
    } catch (err) {
      alert(`Error submitting request: ${err.message}`);
      handleCloseModal();
    } finally {
      setIsSubmitting(false);
    }
  };

  // Derived localization maps. Rebuilt on language change without refetching.
  // - specimenTypeMap is keyed by code (the persisted form on the test record).
  // - clinicalIndicationMap and speciesMap are keyed by display_name (English),
  //   which is what's stored on the test record per the English-only data
  //   layer policy.
  const specimenTypeMap = useMemo(() => {
    const m = {};
    const lang = i18n.language;
    for (const st of allSpecimenTypes) {
      if (st?.code) {
        m[st.code] = st.translations?.[lang] || st.displayName || st.code;
      }
    }
    return m;
  }, [allSpecimenTypes, i18n.language]);

  const clinicalIndicationMap = useMemo(() => {
    const m = {};
    const lang = i18n.language;
    for (const ci of allClinicalIndications) {
      if (ci?.displayName) {
        m[ci.displayName] = ci.translations?.[lang] || ci.displayName;
      }
    }
    return m;
  }, [allClinicalIndications, i18n.language]);

  const speciesMap = useMemo(() => {
    const m = {};
    const lang = i18n.language;
    for (const sp of allSpecies) {
      if (sp?.displayName) {
        m[sp.displayName] = sp.translations?.[lang] || sp.displayName;
      }
    }
    return m;
  }, [allSpecies, i18n.language]);

  if (loading)
    return <CircularProgress sx={{ display: "block", margin: "2rem auto" }} />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!testData)
    return <Alert severity="warning">{t("editTest.caseNotFound")}</Alert>;

  return (
    <Box sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        <TestReportContent
          test={testData}
          submitterName={submitterName}
          editorName={editorName}
          organizationName={organizationName}
          primaryGuideline={primaryGuideline}
          specimenTypeMap={specimenTypeMap}
          clinicalIndicationMap={clinicalIndicationMap}
          speciesMap={speciesMap}
          revisions={revisions}
          editorNames={editorNames}
        />

        {/* User-facing controls — hidden when this view is embedded
            inside a report page. */}
        {!isReportMode && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              mt: 4,
              pt: 2,
              borderTop: "1px solid #eee",
            }}
          >
            {userRole === "user" && (
              <Button
                variant="contained"
                onClick={handleRequestCorrection}
                disabled={hasOpenRequest}
              >
                {hasOpenRequest
                  ? t("viewTest.correctionPending")
                  : t("viewTest.requestCorrection")}
              </Button>
            )}
            {(userRole === "admin" ||
              userRole === "superuser" ||
              userRole === "pro_user") && (
              <Button
                variant="contained"
                onClick={() =>
                  navigate(`/app/edit-test/${testId}`, {
                    state: location.state,
                  })
                }
              >
                {t("viewTest.editThisTest")}
              </Button>
            )}
            <Button variant="outlined" onClick={() => navigate(-1)}>
              {t("common.back")}
            </Button>
          </Box>
        )}
      </Paper>

      <Dialog open={isModalOpen} onClose={handleCloseModal}>
        <DialogTitle>
          {t("viewTest.requestCorrectionTitle", { caseId: testData.caseId })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{t("viewTest.describeError")}</DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="reason"
            label={t("viewTest.reasonForCorrection")}
            type="text"
            fullWidth
            multiline
            rows={4}
            variant="standard"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>{t("common.cancel")}</Button>
          <Button
            onClick={handleSubmitRequest}
            disabled={isSubmitting || !reason.trim()}
            variant="contained"
          >
            {isSubmitting ? (
              <CircularProgress size={24} />
            ) : (
              t("viewTest.submitRequest")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ViewTest;
